import { BSON } from "bson"
import { defaultDeviceTemplateId, Device } from "web/model/Device"
import { DeviceData } from "web/model/DeviceData"
import { Label } from "web/model/Label"
import { LabelSet } from "web/model/LabelSet"
import { Organization } from "web/model/Organization"
import { User } from "web/model/User"
import { Buffer } from 'buffer';

// DEPRECATED
export class ApiClient {
    constructor(baseUrl: string) {
        this._baseUrl = baseUrl
    }

    // ================ API interactions ====================
    getDevices = async () : Promise<Device[]> => {
        const reply:Device[] = await this.sendGet('/api/devices?limit=1000')
        return reply.map(d => new Device(d))
    }
    
    addDevice = async (device: Device) => {
        const request = {
            id: device.id !== '' ? device.id : null,
            serial: device.serial,
            deviceTemplateId: defaultDeviceTemplateId,
            organizationId: device.organization?.id,
            displayedChannels: device.displayedChannels ?? []
        }

        await this.sendPost('/api/devices', request)
    }

    updateDevice = async (device: Device) => {
        const request = {
            serial: device.serial,
            organizationId: device.organization?.id,
            displayedChannels: device.displayedChannels ?? []
        }

        const url = `/api/devices/${device.serial}`
        await this.sendPatch(url, request)
    }

    deleteDevice = async (device: Device) => {
        await this.sendDelete(`/api/devices/${device.id}`)
    }

    getDeviceData = async (
            device: Device, 
            fromTime: Date, 
            toTime: Date, 
            subsamplingPeriodSec: number|null) : Promise<DeviceData> => {
        const fromTimestamp = fromTime.getTime();
        const toTimestamp = toTime.getTime();
        const subsamplingParam = 
            subsamplingPeriodSec != null ? `&subsamplingPeriodSec=${subsamplingPeriodSec}` : null
        const url = 
            `/api/devices/${device.serial}/data?fromTime=${fromTimestamp}&toTime=${toTimestamp}${subsamplingParam}`
        const reply = await this.sendGet(url)
        return new DeviceData(reply);
    }

    getLabelSetsForDevice = async (deviceSerial: string, fromTime?: Date, toTime?: Date) : Promise<LabelSet[]> => {
        var url = `/api/devices/${deviceSerial}/label-sets`
        if (fromTime && toTime) {
            url = addOptionalQueryParam(url, 'fromTime', fromTime.getTime());
            url = addOptionalQueryParam(url, 'toTime', toTime.getTime());
        }
        const reply = await this.sendGet(url)
        
        if (reply && Array.isArray(reply)) {
            return reply.map(s => new LabelSet(s))
        }

        throw `Reply from '${url}' was not a valid set of label sets.`;
    }

    getLabelSet = async (deviceSerial: string, labelSetId: string): Promise<LabelSet> => {
        const url = `/api/devices/${deviceSerial}/label-sets/${labelSetId}`
        const reply = await this.sendGet(url)
        return new LabelSet(reply)
    }

    addLabelSet = async (deviceSerial: string, labelSet: LabelSet) => {
        const request = {
            name: labelSet.name,
            labels: labelSet.labels.map(label => {
                const {id: _, ...newLabel} = label
                return newLabel
            }),
            userId: labelSet.userId
        }
        const response: LabelSet = await this.sendPostForJson(`/api/devices/${deviceSerial}/label-sets`, request)
        return new LabelSet(response)
    }

    addUserLabel = async (deviceSerial: string, label: Label) => {
        const request = label;
        const response: Label = await this.sendPostForJson(`/api/devices/${deviceSerial}/label-sets/user/labels`, request)
        return new Label(response)
    }

    getLabel = async (labelId: string): Promise<Label> => {
        const url = `/api/labels/${labelId}`
        const reply = await this.sendGet(url)
        return new Label(reply)
    }

    updateLabelSet = async (deviceSerial: string, labelSet: LabelSet) => {
        let request = {
            name: labelSet.name,
            labels: labelSet.labels.map(label => {
                const {id: _, ...newLabel} = label
                return newLabel
            }),
            userId: labelSet.userId
        }
        await this.sendPatch(`/api/devices/${deviceSerial}/label-sets/${labelSet.id}`, request)
    }

    updateLabel = async (deviceSerial: string, label: Label) => {
        await this.sendPatch(`/api/devices/${deviceSerial}/labels/${label.id}`, label)
    }

    deleteLabel = async (deviceSerial: string, labelId: string) => {
        await this.sendDelete(`/api/devices/${deviceSerial}/labels/${labelId}`)
    }

    getUsers = async () : Promise<User[]> => {
        const url = `/api/users`
        const reply = await this.sendGet(url)
        
        if (reply && Array.isArray(reply)) {
            return reply.map(s => new User(s))
        }

        throw `Reply from '${url}' was not a valid set of users.`;
    }

    addUser = async (user: User) => {
        const request = {
            email: user.email,
            firstName: user.firstName,
            middleNames: user.middleNames,
            lastName: user.lastName,
            soapNoteTemplate: user.soapNoteTemplate,
            organizations: user.organizations?.map(o => ({id: o.id})) ?? [],
        }

        await this.sendPostForJson('/api/users', request)
    }

    updateUser = async (user: User) => {
        const request = {
            firstName: user.firstName,
            middleNames: user.middleNames,
            lastName: user.lastName,
            soapNoteTemplate: user.soapNoteTemplate,
            organizations: user.organizations?.map(o => ({id: o.id})) ?? [],
        }

        const url = `/api/users/${user.id}`
        await this.sendPatch(url, request)
    }

    deleteUser = async (user: User) => {
        await this.sendDelete(`/api/users/${user.id}`)
    }

    getLabelData = async (
            labelId: string, 
            fromTime: Date, 
            toTime: Date,
            subsamplingPeriodSec: number|null,
            signal?: AbortSignal) : Promise<DeviceData> => {
        let url = `/api/labels/${labelId}/data`;
        url = addOptionalQueryParam(url, 'fromTime', fromTime.getTime());
        url = addOptionalQueryParam(url, 'toTime', toTime?.getTime());
        url = addOptionalQueryParam(url, 'subsamplingPeriodSec', subsamplingPeriodSec ?? undefined);
        const reply = await this.sendGet(url, signal)
        return new DeviceData(reply);
    }

    getOrganizations = async (nameContains?: string, maxResults?: number) : Promise<Organization[]> => {
        let url = `/api/organizations`
        url = addOptionalQueryParam(url, 'nameContains', nameContains)

        const reply = await this.sendGet(url)
        
        if (reply && Array.isArray(reply)) {
            return reply.map(s => new Organization(s))
        }

        throw `Reply from '${url}' was not a valid set of organizations.`;
    }

    // ================ Basic building blocks ===================

    sendGet = async (url: string, signal?: AbortSignal)  : Promise<any> => {
        const reply = await this.makeRequest(url, 'GET', undefined, undefined, signal)
        return await reply.json()
    }

    sendPostForJson = async (url: string, body?: any, signal?: AbortSignal) : Promise<any> => {
        const reply = await this.makeRequest(url, 'POST', body, undefined, signal)
        return await reply.json()
    }

    sendPost = async (url: string, body?: any, signal?: AbortSignal) : Promise<void> => {
        await this.makeRequest(url, 'POST', body, undefined, signal)
    }

    sendPut = async (url: string, body: any, signal?: AbortSignal) : Promise<void> => {
        await this.makeRequest(url, 'PUT', body, undefined, signal)
    }

    sendPatch = async (url: string, body: any, signal?: AbortSignal) : Promise<void> => {
        await this.makeRequest(url, 'PATCH', body, undefined, signal)
    }

    sendPatchForJson = async (url: string, body: any, signal?: AbortSignal) : Promise<any> => {
        const reply = await this.makeRequest(url, 'PATCH', body, undefined, signal)
        return await reply.json()
    }

    sendDelete = async (url: string, signal?: AbortSignal) : Promise<void> => {
        await this.makeRequest(url, 'DELETE', undefined, undefined, signal)
    }

    // ================ Private ===================
    private _baseUrl:string;

    private makeRequest = async (url: string, method: string, body?: any, accept?: string, signal?: AbortSignal) => {
        const request = {
            method: method,
            credentials: "include",
            signal  // Add signal to the request
        } as RequestInit

        if (body) {
            request.body = JSON.stringify(body)
            request.headers = {
                'Content-Type': 'application/json',
                'Accept': accept,
            } as HeadersInit
        } else {
            request.headers = {
                'Accept': accept,
            } as HeadersInit
        }

        const reply = await fetch(this._baseUrl + url, request)

        if (!reply.ok) {
            if (reply.body) {
                const text = await reply.text()
                throw text
            } else {
                throw `Response: ${reply.status}` 
            }
        }

        // Check if the response is BSON
        if (reply.headers.get('Content-Type')?.includes('application/bson')) {
            // Create a new Response with transformed body
            const arrayBuffer = await reply.arrayBuffer();
            const bsonData = BSON.deserialize(Buffer.from(arrayBuffer));

            // Create a new Response with JSON data
            const jsonBlob = new Blob([JSON.stringify(bsonData)], {
                type: 'application/json'
            });

            return new Response(jsonBlob, {
                status: reply.status,
                statusText: reply.statusText,
                headers: reply.headers
            });
        }
        return reply;
    }
}

function addOptionalQueryParam(oldUrl: string, queryParamName: string, queryParamValue?: string|number) {
    if (queryParamValue === null || queryParamValue === undefined) {
        return oldUrl
    }
    
    let url = oldUrl

    if (url.indexOf('?') >= 0) {
        url += '&'
    } else {
        url += '?'
    }

    url += queryParamName + '='
    url += encodeURIComponent(queryParamValue)
    return url;
}
